import { useEffect, useRef } from "react";
import { useParams, useRouteContext } from "@tanstack/react-router";
import { UseFormReturn, useWatch } from "react-hook-form";
import { Container } from "react-bootstrap";

import { useENRLFormLogic } from "./useENRLFormLogic";
import FormFieldRow from "@/components/shared/forms/FormFieldRow";
import ReadOnlyDateInput from "@/components/shared/forms/ReadOnlyDateInput";
import ReadOnlySelect from "@/components/shared/forms/ReadOnlySelect";
import Select from "@/components/shared/forms/Select";
import DateInput from "@/components/shared/forms/DateInput";
import TextInput from "@/components/shared/forms/TextInput";
import { type ENRL, ENRL_TEMPLATE } from "@/models.ts";
import { useCoreData } from "@/queries.ts";
import { normalizeCodeSetValue, validateMinDate } from "@/utils/util";
import { FiFileText } from "react-icons/fi";

// Helper to format date for input[type="date"] (expects YYYY-MM-DD)
const formatDateForInput = (dateValue: string | undefined | null): string => {
  if (!dateValue) return "";
  // If it's already in YYYY-MM-DD format, return as is
  if (/^\d{4}-\d{2}-\d{2}$/.test(dateValue)) return dateValue;
  // If it has a time component (ISO string), strip it
  if (dateValue.includes("T")) return dateValue.split("T")[0];
  // Try to parse and format
  const date = new Date(dateValue);
  if (isNaN(date.getTime())) return "";
  return date.toISOString().split("T")[0];
};

export function ENRLAddEditForm({
  selectedEnrollment,
  form,
  enrollmentDefaults,
}: {
  selectedEnrollment: ENRL | undefined;
  form: UseFormReturn<ENRL, any, ENRL>;
  enrollmentDefaults?: Partial<ENRL>;
}) {
  const params = useParams({
    shouldThrow: false,
    from: "/$leaid/_error-wrapper/records/child/$entityid",
  });

  const leaContext = useRouteContext({ from: "/$leaid" });
  const viewOnly = !leaContext.hasRole("ENRLEdit");
  const coreData = useCoreData();
  const coreDataRef = useRef(coreData);
  coreDataRef.current = coreData;
  const tooltipMap = leaContext.coreData.tooltipMap.get("ENRL");

  const defaultPreschoolCode =
    enrollmentDefaults?.PreschoolCode ??
    leaContext.currentOrgPreschools[0]?.PreschoolCDSCode ??
    "";

  const newEnrollmentTemplate: ENRL = {
    ...ENRL_TEMPLATE,
    CSPPID: params?.entityid ?? "",
    LocalChildId: enrollmentDefaults?.LocalChildId ?? "",
    PreschoolCode: defaultPreschoolCode,
  };

  const enrollment = selectedEnrollment ?? newEnrollmentTemplate;

  const { endDateRules, endReasonRules } = useENRLFormLogic(form);

  const {
    register,
    control,
    reset,
    formState: { errors },
  } = form;

  const programYear = useWatch({ control, name: "ProgramYear" });

  useEffect(() => {
    // Format dates for input[type="date"] which expects YYYY-MM-DD
    const formattedEnrollment = {
      ...enrollment,
      EnrollmentStartDate: formatDateForInput(enrollment.EnrollmentStartDate),
      EnrollmentEndDate: formatDateForInput(enrollment.EnrollmentEndDate),
      ProgramType: normalizeCodeSetValue(
        enrollment.ProgramType,
        coreDataRef.current.programTypeList
      ),
      ProgramDuration: normalizeCodeSetValue(
        enrollment.ProgramDuration,
        coreDataRef.current.programDurationList
      ),
      ContractType: normalizeCodeSetValue(
        enrollment.ContractType,
        coreDataRef.current.contractTypesOfferedList
      ),
      EligibilityStatus: normalizeCodeSetValue(
        enrollment.EligibilityStatus,
        coreDataRef.current.eligibilityStatusList
      ),
      EnrollmentEndReason: normalizeCodeSetValue(
        enrollment.EnrollmentEndReason,
        coreDataRef.current.studentExitCategoryList
      ),
    };
    reset(formattedEnrollment);
  }, [selectedEnrollment, enrollmentDefaults, reset]);

  const enrollmentEndDate = useWatch({
    control: form.control,
    name: "EnrollmentEndDate",
  });

  const isEdit = !!selectedEnrollment;

  const preschoolOptions = leaContext
    .currentOrgPreschoolsByProgramYear(programYear)
    .map((x) => ({
      Text: x.PreschoolName,
      Value: x.PreschoolCDSCode,
    }));

  return (
    <Container
      className="ps-3 pe-3 overview-container"
      style={{ borderTop: "none", borderRadius: 0 }}
    >
      <div className="form-card-modern mb-3">
        <div className="section-header">
          <FiFileText className="section-icon" />
          <h5 className="section-title">Enrollment Information</h5>
        </div>

        <div className="row g-3">
          <div className="col-md-4">
            <FormFieldRow
              name="CSPPID"
              labelText="CSPPID"
              errors={errors}
              tooltipMap={tooltipMap}
              labelCol={12}
              inputCol={12}
              isRequired
            >
              {(id, name) => (
                <TextInput
                  id={id}
                  disabled={viewOnly}
                  readOnly={!!enrollment.CSPPID}
                  maxLength={50}
                  {...register(name, { required: "CSPPID is required" })}
                />
              )}
            </FormFieldRow>
          </div>

          <div className="col-md-4">
            <FormFieldRow
              name="LocalChildId"
              labelText="Local Child ID (CICN)"
              errors={errors}
              tooltipMap={tooltipMap}
              labelCol={12}
              inputCol={12}
              isRequired
            >
              {(id, name) => (
                <TextInput
                  id={id}
                  disabled={viewOnly}
                  readOnly={!!enrollment.LocalChildId}
                  maxLength={50}
                  {...register(name, {
                    required: "Local Child ID (CICN) is required",
                  })}
                />
              )}
            </FormFieldRow>
          </div>

          <div className="col-md-4">
            <FormFieldRow
              name="ProgramYear"
              labelText="Program Year"
              errors={errors}
              tooltipMap={tooltipMap}
              labelCol={12}
              inputCol={12}
              isRequired
            >
              {(id, name) =>
                !!enrollment.ProgramYear && !viewOnly ? (
                  <ReadOnlySelect
                    id={id}
                    control={control}
                    name={name}
                    rules={{ required: "Program Year is required" }}
                    options={coreData.programYearList}
                  />
                ) : (
                  <Select
                    id={id}
                    disabled={viewOnly}
                    {...register(name, {
                      required: "Program Year is required",
                    })}
                    options={coreData.programYearList}
                  />
                )
              }
            </FormFieldRow>
          </div>
        </div>

        <div className="row g-3">
          <div className="col-md-4">
            <FormFieldRow
              name="PreschoolCode"
              labelText="Preschool"
              errors={errors}
              tooltipMap={tooltipMap}
              labelCol={12}
              inputCol={12}
              isRequired
            >
              {(id, name) =>
                isEdit && !viewOnly ? (
                  <ReadOnlySelect
                    id={id}
                    control={control}
                    name={name}
                    rules={{ required: "Preschool is required" }}
                    options={preschoolOptions}
                  />
                ) : (
                  <Select
                    id={id}
                    disabled={viewOnly}
                    {...register("PreschoolCode", {
                      required: "Preschool is required",
                    })}
                    options={preschoolOptions}
                  />
                )
              }
            </FormFieldRow>
          </div>
          <div className="col-md-4">
            <FormFieldRow
              name="ContractType"
              labelText="Contract Type"
              errors={errors}
              tooltipMap={tooltipMap}
              labelCol={12}
              inputCol={12}
              isRequired
            >
              {(id, name) => (
                <Select
                  id={id}
                  disabled={viewOnly}
                  {...register(name, { required: "Contract Year is required" })}
                  options={coreData.contractTypesOfferedList}
                />
              )}
            </FormFieldRow>
          </div>

          <div className="col-md-4">
            <FormFieldRow
              name="ProgramType"
              labelText="Program Type"
              errors={errors}
              tooltipMap={tooltipMap}
              labelCol={12}
              inputCol={12}
              isRequired
            >
              {(id, name) => (
                <Select
                  id={id}
                  disabled={viewOnly}
                  {...register(name, { required: "Program Type is required" })}
                  options={coreData.programTypeList}
                />
              )}
            </FormFieldRow>
          </div>
        </div>

        <div className="row g-3">
          <div className="col-md-4">
            <FormFieldRow
              name="ProgramDuration"
              labelText="Program Duration"
              errors={errors}
              tooltipMap={tooltipMap}
              labelCol={12}
              inputCol={12}
            >
              {(id, name) => (
                <Select
                  id={id}
                  disabled={viewOnly}
                  {...register(name)}
                  options={coreData.programDurationList}
                />
              )}
            </FormFieldRow>
          </div>

          <div className="col-md-4">
            <FormFieldRow
              name="EligibilityStatus"
              labelText="Eligibility Status"
              errors={errors}
              tooltipMap={tooltipMap}
              labelCol={12}
              inputCol={12}
              isRequired
            >
              {(id, name) => (
                <Select
                  id={id}
                  disabled={viewOnly}
                  {...register(name, {
                    required: "Eligiblity Status is required",
                  })}
                  options={coreData.eligibilityStatusList}
                />
              )}
            </FormFieldRow>
          </div>

          <div className="col-md-4">
            <FormFieldRow
              name="EnrollmentStartDate"
              labelText="Enrollment Start Date"
              errors={errors}
              tooltipMap={tooltipMap}
              labelCol={12}
              inputCol={12}
              isRequired
            >
              {(id, name) =>
                !!enrollment.EnrollmentStartDate && !viewOnly ? (
                  <ReadOnlyDateInput
                    id={id}
                    control={control}
                    name={name}
                    rules={{
                      required: "Enrollment Start Date is required",
                      validate: validateMinDate,
                    }}
                  />
                ) : (
                  <DateInput
                    id={id}
                    disabled={viewOnly}
                    max="9999-12-31"
                    {...register("EnrollmentStartDate", {
                      required: "Enrollment Start Date is required",
                      validate: validateMinDate,
                    })}
                  />
                )
              }
            </FormFieldRow>
          </div>
        </div>

        <div className="row g-3">
          <div className="col-md-6">
            <FormFieldRow
              name="EnrollmentEndDate"
              labelText="Enrollment End Date"
              errors={errors}
              tooltipMap={tooltipMap}
              labelCol={12}
              inputCol={12}
            >
              {(id, name) => (
                <DateInput
                  id={id}
                  disabled={viewOnly}
                  max="9999-12-31"
                  {...register(name, endDateRules)}
                />
              )}
            </FormFieldRow>
          </div>

          <div className="col-md-6">
            <FormFieldRow
              name="EnrollmentEndReason"
              labelText="Enrollment End Reason"
              errors={errors}
              tooltipMap={tooltipMap}
              labelCol={12}
              inputCol={12}
              isRequired={!!enrollmentEndDate}
            >
              {(id, name) => (
                <Select
                  id={id}
                  disabled={viewOnly}
                  {...register(name, endReasonRules)}
                  options={coreData.studentExitCategoryList}
                />
              )}
            </FormFieldRow>
          </div>
        </div>
      </div>

      <span
        id="spnAddOrEditClassroomErrorOverall"
        className="row ps-3 mt-3 pe-3 float-end"
        style={{ color: "#CC0000" }}
      ></span>
    </Container>
  );
}
